import { CheckoutPaymentTypes } from '../_helpers/checkout-payment-types';
import {Constants} from '../constants';
import {MenuItem} from './order';
import {AdditionalTaxSummary} from './additional-tax';
import {AutoGratuityRule} from './auto-gratuity';

export class  Account {
  id:string;
  company: string;
  token: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  ftp_username: string;
}

export class StoreData {
  /** Store-wide POS menu button text size (compact/default/large/xlarge); PATCHed at the store's top level. */
  pos_menu_text_size: string;
  /** ENG-5842: store default of the POS Text size / Button size sliders, whole percents; null = never set. */
  pos_text_pct?: number | null;
  pos_button_pct?: number | null;
  /** ENG-5842: the left order panel's text size; null = follows pos_text_pct. */
  pos_summary_text_pct?: number | null;
  employee_state_tax_percent: number;
  employee_federal_tax_percent: number;
  tip_value_min_order: number;
  tip_values: any[];
  order_comfirmation_message: any;
  enable_training_mode: boolean;
  order_app_toggle_tip: boolean;
  occasion_list_order: string;
  /** POS checkout buttons: display order and the store's own payment types (Venmo, Check...). */
  checkout_payment_types?: CheckoutPaymentTypes;
  // ENG-5252: customer self-serve online order cancellation
  enable_customer_order_cancel: boolean;
  customer_order_cancel_window: number;
  // ENG-5315 / ENG-5331: the catering access-code gate is saved on store_data, not the store row.
  enable_catering_gate: boolean;
  catering_gate_code: string;
  future_order_cancel_window_minutes: number;
  // ENG-5243: the occasion and time a web order starts on, set from the Configuration tab's
  // Online order settings row.
  default_online_occasion: string;
  default_online_time: string;
  // ENG-5687: merchant-chosen order of the side nav and the Orders tabs (lists of item keys,
  // see _models/ui-order.ts). Empty = built-in order.
  nav_order: string[];
  orders_tab_order: string[];
  // ENG-5886: per-template, per-section receipt look ({template: {section: {align, size, format, gap, rule}}}).
  receipt_layout: any;
  /** ENG-5974: tips above this percent of the check need a manager's PIN; null = off (1-500). */
  tip_approval_threshold_percent?: number | null;
}
export class  Store {
  id:string;
  account: Account;
  // ENG-5331: how catering is presented in the ordering apps.
  catering_label: string;
  catering_icon: string;
  store_name: string;
  store_number: number;
  address: string;
  state_geographic:string;
  phone: string;
  secondary_phone:string;
  ivr_phone:string;
  connect_number:string;
  sms_number: string;
  sms_webhook: string;
  time_zone: string;
  contact_name: string;
  contact_phone:string;
  contact_email:string;
  has_order_time_now:boolean;
  has_order_time_later:boolean;
  has_order_occasion_pickup:boolean;
  has_order_occasion_delivery:boolean;
  has_order_occasion_dine_in:boolean;
  has_order_occasion_curbside:boolean;
  has_order_occasion_tableside:boolean;
  has_order_occasion_togo:boolean;
  has_order_occasion_forhere:boolean;
  has_order_occasion_drivethru:boolean;
  pickup_dine_in_lead_time:number;
  pin_timeout: number;
  howl_pin: string;
  /** Station rows: an array (one per terminal) on `multiple_default_load` stores, else one
      object. Each carries default_load / closed_orders / open_orders and, since ENG-5925,
      `server_station` (no cash drawer on that station). */
  pos_setting:any;
  dine_in_lead_time:number;
  delivery_lead_time:number;
  delivery_radius:number;
  delivery_charge:number;
  delivery_minimum:number;
  gmb_location_id:string;
  gmb_menu_url:string;
  gmb_order_url:string;
  service_toggle:boolean;
  has_menu:boolean;
  printouts_per_order:number;
  working:Map<string, WorkingDay>;
  voided_check:any;
  verified:boolean;
  has_structured_menu:boolean;
  enable_parser:boolean;
  printer_id:string;
  ivr_welcome_message:string;
  payment_gateway:string;
  service_charge:number;
  url_slug:string;
  enable_text_ordering:boolean;
  enable_pick_and_click_ordering:boolean;
  printouts_per_delivery_order:number;
  printouts_per_curbside_order:number;
  printouts_per_dine_in_order:number;
  lead_times:any[]
  last_order_buffer: Map<number, number>;
  occasion_flags: Map<string, boolean>;
  modules: string[];
  zip_code;
  city;
  feature_flag:Map<string, boolean>;
  special_hour:any[]
  tip_percent_option: any[];
  service_charge_label:string;
  service_charge_description:string;
  has_card_payment:boolean;
  // ENG-5609: lets a guest whose verification code never arrives still pay, with a card
  // entered for that one order. Off by default: it relaxes a security control.
  allow_guest_checkout:boolean;
  toggle_tip:boolean;
  enable_pos_pay_later:boolean;
  enable_default_service_charge:boolean;
  service_charge_percent_option:any[]
  pos_service_charge_title:string;
  payroc_username: string;
  enable_consumer_choice_tip:boolean;
  enable_consumer_choice:boolean;
  surcharge_label: any;
  surcharge_percent: any;
  // surcharge_label: any;
  surcharge_text: any;
  online_service_charge_title: any;
  default_online_service_charge: any;
  default_pos_service_charge: any;
  local_tax_percent: any;
  state_tax_percent: any;
  // Sales tax on fees (backend order/fee_tax.py). null = follow the store's state.
  tax_service_charge?: boolean | null;
  tax_credit_surcharge?: boolean | null;
  fee_tax?: {
    state: string | null; state_name: string | null; state_default: boolean | null; surcharge_banned: boolean;
    tax_service_charge: boolean | null; tax_service_charge_effective: boolean;
    tax_credit_surcharge: boolean | null; tax_credit_surcharge_effective: boolean;
  };
  /** Active additional taxes (e.g. a 13.5% mixed beverage tax) with their menu assignments. */
  additional_taxes?: AdditionalTaxSummary[];
  /** Active auto-gratuity rules ("parties of 6+ pay 18%"), for registers that price a check locally. */
  auto_gratuity_rules?: AutoGratuityRule[];
  disclaimer_message: any;
  disclaimer_show: boolean;
  disclaimer_from_time: any;
  disclaimer_to_time: any;

  other_payment_label: string;
  enable_instant_cash_discount: boolean;
  instant_cash_discount_percent: number;
  service_fee_threshold_limit: number;
  enable_cash_payment:boolean;
  pos_payment_gateway: string;
  service_charge_label_pos:string;
  //holiday_list:any
  nmi_public_key: string;
  service_charge_pos: any;
  consolidate_taxes_and_fees: any;
  // ENG-5889: flat per-order fee folded into the subtotal (taxable, every tender).
  order_fee_enabled: boolean;
  order_fee_amount: number;
  order_fee_label: string;
  /** 'subtotal' (inside the subtotal, disclaimer below) or 'line' (its own row). */
  order_fee_display: string;
  enable_manual_print_control: boolean;
  /** ENG-5148: Store-wide POS display size preset id (compact/default/large/xlarge). */
  pos_ui_scale: string;
  /** Store-wide POS menu button text size preset id (small/default/large/xlarge) — APK PR #7 parity. */
  pos_menu_text_size: string;
  /** ENG-5842: PATCH-only top-level keys; the store serves them inside store_data. */
  pos_text_pct?: number | null;
  pos_button_pct?: number | null;
  pos_summary_text_pct?: number | null;
  /** ENG-5292: Points awarded per approved customer photo submission. */
  customer_photo_points: number;
  enable_pos_credit_service_charge_dollar: boolean;
  enable_online_credit_service_charge_dollar: boolean;
  surcharge_label_dollar: any;
  pos_surcharge_percent_label: any;
  pos_surcharge_percent: any;
  surcharge_dollar: any;
  pos_surcharge_dollar_label: any;
  pos_surcharge_dollar: any;
  store_pause_time: any;
  pause_store: boolean;
  pause_store_whole_day: boolean;
  store_pause_date_time: any;
  store_whole_day_pause_date_time: any;
  gift_card_default_image: any;
  pos_misc_button_color: string;
  pos_misc_button_text_color: string;
  pos_gift_card_button_color: string;
  pos_gift_card_button_text_color: string;
  storefront_theme: string;
  storefront_brand_color: string;
  storefront_dark_mode: boolean;
  storefront_corner_style: string;
  store_type: any;
  occasion_pickup_message: any;
  occasion_dinein_message: any;
  occasion_delivery_message: any;
  occasion_curbside_message: any;
  occasion_tableside_message: any;
  store_clock_out_time: number;
  store_data:StoreData;
  holiday_list:any[]
  occasion_flags_online: any;
  occasion_flags_catering: any;
  ui_settings: any;

  getServiceChargeLabel(){
    return this.service_charge_label_pos && this.service_charge_label_pos.length > 0 ? this.service_charge_label_pos : 'Service Fee'
  }

  getSurchargeLabel(){
    if (this.enable_pos_credit_service_charge_dollar || this.enable_online_credit_service_charge_dollar) {
      return this.surcharge_label_dollar && this.surcharge_label_dollar.length > 0 ? this.surcharge_label_dollar : 'Surcharge'
    } else {
      return this.surcharge_label && this.surcharge_label.length > 0 ? this.surcharge_label : 'Surcharge'
    }

  }

  getTodaysWorkingConfig(){
    let today = new Date()
    var dayIndex = today.getDay() - 1;
    if(dayIndex < 0){
      dayIndex = 6;
    }
    return this.getSlot(dayIndex)
  }

  getSlot(dayIndex){
    let workingHours = this.working[dayIndex]['hours']
    var slot;

    if(workingHours.length === 1){
      slot =  workingHours[0]
    }else{
      let now = new Date()
      slot =  workingHours.find(w => w.from_hour >= now.getHours())
      if(!slot){
        slot = workingHours[workingHours.length - 1]
      }
    }
    slot['is_open'] = this.working[dayIndex]['open_type'] !== 'CLOSED'
    return slot;
  }

  getFeatureFlag(key){
    if(!this.feature_flag){
      return false;
    }
    if(key in this.feature_flag){
      return this.feature_flag[key]
    }else{
      return false;
    }
  }
  getTodayIndex(){
    let today = new Date()
    var dayIndex = today.getDay() - 1;
    if(dayIndex < 0){
      dayIndex = 6;
    }
    return dayIndex;
  }

  getLeadTime(occasion) {
    let leadTime = 0;
    let now = new Date();
    let dayIndex = this.getTodayIndex()
    if(occasion === 'PICKUP' || occasion === 'CURBSIDE'){
      leadTime = this.pickup_dine_in_lead_time;
    }else if(occasion === 'DELIVERY') {
      leadTime = this.delivery_lead_time;
    }

    let leadTimes:any[] = this.lead_times.filter(x => x.day === dayIndex &&  x.occasion === Constants.occasionRi[occasion])

    leadTimes.forEach(lt => {
      let start = new Date()
      start.setHours(lt.from_hour)
      start.setMinutes(lt.from_min)
      let end = new Date()
      end.setHours(lt.to_hour)
      end.setMinutes(lt.to_min)
      if(start < now && end > now){
        leadTime = lt.lead_time;
      }
    });
    return leadTime;
  }

  hasModule(module){
    return this.modules.includes(module)
  }

  getOccasions(){
    const filteredOccasions: string[] = [];
    Object.entries(this.occasion_flags).forEach(
      ([occasion, available]) => available && filteredOccasions.push(occasion)
    );
    return filteredOccasions;
  }

  /** POS occasion list WITHOUT the occasion-hours gate.
   *
   *  Background: the backend builds `occasion_flags` as
   *  "enabled for POS  AND  current store-local time is inside that
   *  occasion's own configured hours". Merchants asked for the hours half
   *  to be dropped on POS only: if an occasion is enabled for POS, the
   *  cashier should always see the pill.
   *
   *  `occasion_flags` cannot be loosened on the backend because the same
   *  serializer field is consumed by the online ordering web app, kiosk,
   *  bot orders and the driver app. So the POS-only rule is rebuilt here
   *  from the raw `has_order_occasion_*` toggles, which are already on the
   *  store payload. Nothing about the API response changes.
   *
   *  Mirrors the backend's non-hours rules exactly:
   *    - PICKUP / DELIVERY / DINE IN / CURBSIDE / TABLESIDE -> POS toggle
   *    - TO GO / FOR HERE / DRIVE THRU -> POS toggle AND the store having the POS module
   *    - display order comes from store_data.occasion_list_order
   *
   *  Gated by the `bypass_pos_occasion_hours` feature flag at the call
   *  sites; when the flag is off, callers keep using `occasion_flags`.
   */
  getPosOccasionsIgnoringHours(): string[] {
    // Backend default when store_data.occasion_list_order is unset.
    const DEFAULT_ORDER = [
      'has_order_occasion_pickup',
      'has_order_occasion_dine_in',
      'has_order_occasion_tableside',
      'has_order_occasion_delivery',
      'has_order_occasion_curbside',
      'has_order_occasion_togo',
      'has_order_occasion_forhere',
      'has_order_occasion_drivethru',
    ];

    // occasion_list_order arrives as a Python-style list string with single
    // quotes, e.g. "['has_order_occasion_pickup', ...]" — same parse the
    // store-info screen already does.
    let fieldOrder: string[] = [];
    const orderStr = this.store_data?.occasion_list_order;
    if (orderStr) {
      try {
        fieldOrder = JSON.parse(String(orderStr).replace(/'/g, '"'));
      } catch (e) {
        fieldOrder = [];
      }
    }
    if (!Array.isArray(fieldOrder) || !fieldOrder.length) {
      fieldOrder = DEFAULT_ORDER;
    }
    fieldOrder = withNewOccasionKeys(fieldOrder);

    // Some of these toggles come back as "0"/"1" strings rather than
    // booleans, so a plain truthy check would treat "0" as enabled.
    const truthy = (v: any): boolean =>
      !!(typeof v === 'string' ? Number(v) : v);

    const hasPos = (this.modules || []).includes('POS');
    // Indexed by field name below; cast once to avoid an implicit-any index
    // signature error on the typed class.
    const self = this as any;
    const out: string[] = [];

    fieldOrder.forEach((field: string) => {
      const label = OCCASION_LABELS_TO_FLAG_REVERSE[field];
      if (!label || out.indexOf(label) !== -1) { return; }

      // TO GO / FOR HERE / DRIVE THRU are POS-only occasions: the backend
      // requires the toggle AND the POS module (the module check cannot
      // resurrect an occasion whose toggle is off).
      if (POS_ONLY_OCCASION_KEYS.includes(field)) {
        if (hasPos && truthy(self[field])) { out.push(label); }
        return;
      }

      if (truthy(self[field])) { out.push(label); }
    });

    return out;
  }

  getSource(){
    let sources = []
    if(this.enable_text_ordering){
      sources.push('TEXT');
    }

    if(this.hasModule('POS')){
      sources.push('POS');
    }

    // Kiosk is its own module — stores can run a kiosk without POS, so gating
    // this on POS hid the source filter for kiosk-only stores.
    if(this.hasModule('KIOSK_MODULE') || this.hasModule('POS')){
      sources.push('KIOSK');
    }

    if(this.enable_pick_and_click_ordering){
      sources.push('ONLINE');
    }
    return sources;
  }

  getCloseStatusTypes(){
    let closeStatusTypes = [];
    closeStatusTypes.push(...['CLOSED','CANCELLED','REFUND','TIP_ADD','ADJUSTMENT'])
    
    return closeStatusTypes;
  }

  getOpenStatusTypes() {
    let openStatusTypes = [];
    openStatusTypes.push(...['OPEN'])
    
    return openStatusTypes;
  }

  getPaymentTypes(){
    let paymentTypes = [];
    if(this.has_card_payment){
      paymentTypes.push('CREDIT');
    }

    if(this.enable_cash_payment || (this.modules.length > 1 && this.modules.includes('POS'))){
      paymentTypes.push('CASH');
    }

    paymentTypes.push(...['STORE_CREDIT', 'SPLIT', 'GIFT', 'CREDIT_KEYED', 'PAY_LATER', 'OTHER']);
    return paymentTypes;
  }

  getOrderTiming(){
    let order_time = [];
    if(this.has_order_time_now){
      order_time.push('NOW');
    }

    if(this.has_order_time_later){
      order_time.push('LATER');
    }
    return order_time;
  }
}

export class PosSetting{
  default_load:boolean;
  closed_orders:boolean;
  open_orders:boolean;
  default_order_occasion:string;
  default_time:string;
  id:number;
  store:Store;
}


export class WorkingDay{
  is_open:boolean;
  from_hour:number;
  from_min:number;
  to_hour:number;
  to_min:number;

  get12HrStartTime() {
    return this.get12HourString(this.from_hour, this.from_min)
  }

  get12HrEndTime(){
    return this.get12HourString(this.to_hour, this.to_min)
  }

  get12HourString(hour: number, min: number) {
    const hours = hour > 12 ? hour / 12 : hour;
    const mins = min === 0 ? '00' : min;
    return hours + ' : ' + mins + ' ' +  (hour > 12 ? 'PM' : 'AM') ;
  }
}

export class StoreMenuGroup{
  group_name:string;
  items: MenuItem[];
}
export class StoreMenuItems{
  menu_items:StoreMenuGroup[];
  store:Store;
  menu_order:string[];
}

export class MenuItemGroup{
  id: number;
  name:string;
  description:string;
  starting_price:number;
  is_modifier_subgroup?: boolean;

}
export class StoreMenuItemsV2{
  menu_items:Map<string, MenuItemGroup>;
  store:Store;
  menu_order:string[];
}

export class CoPilotMerchant{
  id:number;
  dba_name: string;
  legal_business_name: string;
  tax_filing_method: string;
  tax_filing_name: string;
  website_address: string;
  business_phone:string;
  business_address_address: string;
  business_address_city: string;
  business_address_state: string;
  business_address_zip: string;
  mailing_address_address: string;
  mailing_address_city: string;
  mailing_address_state:string;
  mailing_address_zip: string;
  deposit_bank_name: string;
  deposit_bank_account_number: string;
  deposit_bank_account_type: string;
  deposit_bank_routing_number: string;
  withdrawal_bank_name: string;
  withdrawal_bank_account_number: string;
  withdrawal_bank_account_type: string;
  withdrawal_bank_routing_number: string;
  owner_name: string;
  owner_type: string;
  owner_title: string;
  owner_email: string;
  owner_ssn: string;
  owner_phone: string;
  owner_mobile_phone: string;
  owner_address: string;
  owner_city: string;
  owner_address_state:string;
  owner_zip: string;
  voided_check: string;
  merchant_id: string;
  signature_url: string;
  gateway_boarding_status: string;
  boarding_process_status: string;
  created_at: string;
  updated_at: string;
}

export class StoreMerchant{
  id:number;
  store:Store;
  copilot_merchant:CoPilotMerchant;
}


/** What a printer is for; derived server-side from the routing flags below. */
export type PrinterType = 'STATION' | 'KITCHEN' | 'OPERATIONAL';

export class PrinterSetting{
  id: number;
  store: number;
  name: string;
  printer_id: string;
  pickup_count: number;
  delivery_count: number;
  curbside_count: number;
  dine_in_count: number;
  tableside_count: number;
  kitchen_count: number;
  has_kitchen_format: boolean
  kitchen_language: string;
  /** Kitchen tickets for the menu categories assigned to it. */
  is_kitchen_printer: boolean;
  /** Sales receipts for the POS station it is named after. */
  is_sales_printer: boolean;
  /** Receives the customer receipt for online and third-party (DoorDash, UberEats) orders. */
  is_operational_printer: boolean;
  /** Fallback when an order has no station printer of its own; one per store. */
  is_default: boolean;
  /** POS terminal id for station printers. */
  station: number | null;
  printer_type: PrinterType;
  menu_category: Array<{ id: number; menu_category: string }>;
  occasion: Array<{ id: number; occasion: string }>;
  tableside_table: Array<{ id: number; table_name: string }>;
  created_at: Date;
  updated_at: Date;
  heartbeat: PrinterHeartbeat;
}


export class PrinterHeartbeat{
  store: number;
  printer_id: string;
  last_seen: Date;
  is_online: boolean
  name: string;
  created_at: Date;
  updated_at: Date;
}

export const MenuResetOptions = [
  { key: 1, value: 'Never' },
  { key: 2, value: 'In one hour' },
  { key: 3, value: 'Start of next day ' },
  { key: 4, value: 'Start of next week' },
  { key: 5, value: 'Start of next month' },
  { key: 6, value: 'On deck' }
];

export const MenuCategory: { key: string; name: string }[] = [];

export class IncomingCall {
  id: number;
  call_id: string;
  customer_name: string;
  is_existing_customer: boolean;
  phone_number: string;
  store_id: number;
}

export const OCCASION_REVERSE_MAP: { [key: string]: string } = {
  'PICKUP': 'has_order_occasion_pickup',
  'DELIVERY': 'has_order_occasion_delivery',
  'DINE IN': 'has_order_occasion_dine_in',
  'CURBSIDE': 'has_order_occasion_curbside',
  'TABLESIDE': 'has_order_occasion_tableside',
  'TO GO': 'has_order_occasion_togo',
  'FOR HERE': 'has_order_occasion_forhere',
  'DRIVE THRU': 'has_order_occasion_drivethru',
};

/** has_order_occasion_* field -> POS occasion label.
 *  Inverse of OCCASION_REVERSE_MAP. Used by
 *  Store.getPosOccasionsIgnoringHours() to turn the merchant's saved
 *  occasion_list_order (a list of field names) into display labels.
 *  Mirrors `occasion_mapping` in the backend store serializer. */
export const OCCASION_LABELS_TO_FLAG_REVERSE: { [key: string]: string } = {
  'has_order_occasion_pickup': 'PICKUP',
  'has_order_occasion_delivery': 'DELIVERY',
  'has_order_occasion_dine_in': 'DINE IN',
  'has_order_occasion_curbside': 'CURBSIDE',
  'has_order_occasion_tableside': 'TABLESIDE',
  'has_order_occasion_togo': 'TO GO',
  'has_order_occasion_forhere': 'FOR HERE',
  'has_order_occasion_drivethru': 'DRIVE THRU',
};

export const OCCASION_LABELS: { [key: string]: string } = {
  has_order_occasion_pickup: 'Pickup',
  has_order_occasion_pickup_online: 'Pickup',
  has_order_occasion_delivery: 'Delivery',
  has_order_occasion_delivery_online: 'Delivery',
  has_order_occasion_dine_in: 'Dine In',
  has_order_occasion_dine_in_online: 'Dine In',
  has_order_occasion_curbside: 'Curbside',
  has_order_occasion_curbside_online: 'Curbside',
  has_order_occasion_tableside: 'Tableside',
  has_order_occasion_tableside_online: 'Tableside',
  has_order_occasion_togo: 'To Go',
  has_order_occasion_forhere: 'For Here',
  has_order_occasion_drivethru: 'Drive Thru',
};

/** Occasions that only exist on the POS: no online or catering column, and hidden for
 *  stores without the POS module. */
export const POS_ONLY_OCCASION_KEYS: string[] = [
  'has_order_occasion_togo',
  'has_order_occasion_forhere',
  'has_order_occasion_drivethru',
];

/** Occasions with no kiosk column (a drive-through window is never a kiosk order). */
export const NO_KIOSK_OCCASION_KEYS: string[] = ['has_order_occasion_drivethru'];

/** A merchant's saved occasion_list_order can predate Drive Thru. Append it so it can still
 *  be switched on; the backend store serializer does the same for occasion_flags. */
export function withNewOccasionKeys(keys: string[]): string[] {
  return keys.includes('has_order_occasion_drivethru') ? keys : [...keys, 'has_order_occasion_drivethru'];
}
